import DatePicker from "../../components/DatePicker"
import { Box, Grid, InputAdornment, Typography } from "@material-ui/core"
import { convertHHmmToDate, getDurationInHoursAndMinutes } from "../utils"
import { Clock } from "react-feather"
import { isValid } from "date-fns"

type IntervalEntryFieldsProps = {
  date: Date | string
  start: Date | string
  onStartChange: (date: Date | null) => void
  startLabel: string
  startError?: string
  end: Date | string
  onEndChange: (date: Date | null) => void
  endLabel: string
  endError?: string
  durationDescription: (hours: number, minutes: number) => string
  isClearable?: boolean
}

const IntervalEntryFields = ({
  date,
  start,
  onStartChange,
  startLabel,
  startError,
  end,
  onEndChange,
  endLabel,
  endError,
  durationDescription,
  isClearable = true,
}: IntervalEntryFieldsProps) => {
  const isoDate = new Date(date).toISOString()
  const startAsDate = start instanceof Date ? start : asValidDate(isoDate, start)
  const endAsDate = end instanceof Date ? end : asValidDate(isoDate, end)
  const { hours, minutes } = getDuration(startAsDate, endAsDate)

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      date.setSeconds(0, 0) // Reset seconds and milliseconds to avoid issues with time calculations
      onStartChange(date)
    } else {
      onStartChange(null)
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      date.setSeconds(0, 0) // Reset seconds and milliseconds to avoid issues with time calculations
      onEndChange(date)
    } else {
      onEndChange(null)
    }
  }

  return (
    <>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6}>
          <DatePicker
            onChange={handleStartDateChange}
            selected={startAsDate}
            dateFormat={"HH:mm"}
            placeholderText="HH:mm"
            timeIntervals={30}
            textFieldProps={{
              error: Boolean(startError),
              helperText: startError,
              label: startLabel,
              InputLabelProps: { shrink: true },
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Clock size={20} />
                  </InputAdornment>
                ),
              },
            }}
            showTimeSelect
            showTimeSelectOnly
            isClearable={isClearable}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            onChange={handleEndDateChange}
            selected={endAsDate}
            dateFormat={"HH:mm"}
            placeholderText="HH:mm"
            timeIntervals={30}
            textFieldProps={{
              label: endLabel,
              InputLabelProps: { shrink: true },
              error: Boolean(endError),
              helperText: endError,
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Clock size={20} />
                  </InputAdornment>
                ),
              },
            }}
            showTimeSelect
            showTimeSelectOnly
            isClearable={isClearable}
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
          {durationDescription(hours, minutes)}
        </Typography>
      </Box>
    </>
  )
}

export default IntervalEntryFields

const asValidDate = (date: string, HHmm: string): Date | null => {
  if (!date || !HHmm) return null
  const dateInstance = convertHHmmToDate({ HHmm, date: new Date(date) })
  if (!isValid(dateInstance)) return null
  return dateInstance
}

const defaultDuration = { hours: 0, minutes: 0 }

const getDuration = (start: Date | null, end: Date | null): { hours: number; minutes: number } => {
  const hasDuration = Boolean(start && end)
  if (!hasDuration) return defaultDuration
  return getDurationInHoursAndMinutes(start!, end!)
}
